import React, { useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';

// Gráficas del Inicio: "cómo va hoy" comparado con tu semana.
// Una sola serie por gráfica (ventas), con el color de marca; hoy va en tono
// lleno y los demás días en tono suave. Tocar/pasar sobre una barra muestra el detalle.

const ACENTO = 'var(--k-accent, var(--color-primario, #6D28D9))';
const TINTA = 'var(--k-ink, #1F2937)';
const SUAVE = 'var(--k-muted, #6B7280)';
const LINEA = 'var(--k-hairline, #ECE7F5)';

const dinero = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const corto = (n) => {
  const v = Number(n || 0);
  const t = v >= 1000 ? `${(v / 1000).toLocaleString('es-HN', { maximumFractionDigits: 1 })} mil` : v.toLocaleString('es-HN', { maximumFractionDigits: 0 });
  return `${simboloMoneda()} ${t}`;
};
const horaTxt = (h) => `${h % 12 || 12} ${h < 12 ? 'a. m.' : 'p. m.'}`;
const horaCorta = (h) => `${h % 12 || 12}${h < 12 ? 'am' : 'pm'}`;

// Barra con las esquinas de arriba redondeadas (4px) y base plana en el eje.
function barra(x, y, w, h, r = 4) {
  if (h <= 0) return '';
  const rr = Math.min(r, w / 2, h);
  return `M${x},${y + h}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${y + h}Z`;
}

// Escala "bonita" para el eje: 0 · mitad · tope.
function tope(max) {
  if (max <= 0) return 100;
  const p = 10 ** Math.floor(Math.log10(max));
  const pasos = [1, 2, 2.5, 5, 10];
  return pasos.map((k) => k * p).find((v) => v >= max) || 10 * p;
}

// Gráfica de columnas genérica con tooltip por barra.
function Columnas({ datos, alto = 170, etiquetaX, tooltip, resaltar, vacia }) {
  const [activo, setActivo] = useState(null);
  const W = 560; const H = alto; const izq = 44; const der = 8; const arriba = 12; const abajo = 24;
  const anchoUtil = W - izq - der; const altoUtil = H - arriba - abajo;
  const max = tope(Math.max(...datos.map((d) => d.valor)));
  const paso = anchoUtil / datos.length;
  const ancho = Math.max(6, Math.min(40, paso * 0.62));
  const y = (v) => arriba + altoUtil - (v / max) * altoUtil;
  const d = activo != null ? datos[activo] : null;

  return (
    <div style={{ position: 'relative' }} onMouseLeave={() => setActivo(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={vacia || 'Gráfica de ventas'} style={{ display: 'block', overflow: 'visible' }}>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={izq} x2={W - der} y1={y(max * f)} y2={y(max * f)} stroke={LINEA} strokeWidth="1" />
            <text x={izq - 6} y={y(max * f) + 4} textAnchor="end" fontSize="11" fill={SUAVE}>{corto(max * f)}</text>
          </g>
        ))}
        {datos.map((p, i) => {
          const x = izq + paso * i + (paso - ancho) / 2;
          const fuerte = resaltar(p);
          return (
            <g key={i}>
              <path d={barra(x, y(p.valor), ancho, y(0) - y(p.valor))}
                style={{ fill: ACENTO, fillOpacity: fuerte ? 1 : activo === i ? 0.55 : 0.32 }} />
              {etiquetaX(p, i) && (
                <text x={x + ancho / 2} y={H - 6} textAnchor="middle" fontSize="11"
                  fill={fuerte ? TINTA : SUAVE} fontWeight={fuerte ? 700 : 400}>{etiquetaX(p, i)}</text>
              )}
              {/* Zona de toque más grande que la barra */}
              <rect x={izq + paso * i} y={arriba} width={paso} height={altoUtil + abajo} fill="transparent"
                style={{ cursor: 'pointer' }} onMouseEnter={() => setActivo(i)} onClick={() => setActivo(i)} />
            </g>
          );
        })}
      </svg>
      {vacia && (
        <div style={{ position: 'absolute', inset: `0 0 ${abajo}px ${izq}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <span style={{ fontSize: '13px', color: SUAVE, backgroundColor: 'rgba(255,255,255,0.9)', padding: '6px 10px', borderRadius: '8px', textAlign: 'center' }}>{vacia}</span>
        </div>
      )}
      {d && (
        <div style={{ position: 'absolute', top: 0, left: `${((izq + paso * activo + paso / 2) / W) * 100}%`,
          transform: `translateX(${activo > datos.length / 2 ? '-100%' : '0'})`, pointerEvents: 'none',
          backgroundColor: 'white', border: `1px solid ${LINEA}`, borderRadius: '10px', padding: '8px 10px',
          boxShadow: '0 6px 18px rgba(30,20,60,0.12)', fontSize: '12px', color: TINTA, whiteSpace: 'nowrap', zIndex: 2 }}>
          {tooltip(d)}
        </div>
      )}
    </div>
  );
}

export default function HoyGraficas({ datos, verGastos = true, verCostos = true }) {
  const { dias, promedioDiario, porHora, numVentasHoy, mejorHora, totalHoy } = datos;

  // Rango de horas: el horario normal (8 am – 8 pm) estirado si hubo ventas fuera.
  const horaActual = new Date().getHours();
  const conVentas = porHora.filter((h) => h.monto > 0).map((h) => h.hora);
  const desde = Math.min(8, ...conVentas);
  const hasta = Math.max(20, horaActual, ...conVentas);
  const horas = porHora.slice(desde, hasta + 1).map((h) => ({ ...h, valor: h.monto }));

  const pct = promedioDiario > 0 ? Math.round((totalHoy / promedioDiario) * 100) : null;
  const escala = Math.max(totalHoy, promedioDiario) * 1.15 || 1;
  const ticketProm = numVentasHoy > 0 ? totalHoy / numVentasHoy : 0;

  const diasSerie = dias.map((d) => ({ ...d, valor: d.ventas }));
  const nombreDia = (d) => d.dia.toLocaleDateString('es-HN', { weekday: 'short' }).replace('.', '');

  const tarjeta = { backgroundColor: 'white', borderRadius: '14px', padding: '16px', border: `1px solid ${LINEA}` };

  return (
    <div className="k-card" style={{ padding: '18px' }}>
      <p className="k-sec-title" style={{ marginBottom: '14px' }}>📈 Cómo va hoy</p>

      {/* Hoy contra tu promedio diario */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '6px 16px', alignItems: 'baseline', marginBottom: '8px' }}>
          <span style={{ fontSize: '14px', color: TINTA, fontWeight: 600 }}>
            {pct == null
              ? 'Aún no hay ventas de días anteriores para comparar.'
              : pct >= 100
                ? `🎉 ¡Ya superaste tu promedio diario! Vas al ${pct}%`
                : `Vas al ${pct}% de tu promedio diario`}
          </span>
          <span style={{ fontSize: '12px', color: SUAVE }}>
            {numVentasHoy} venta(s) hoy{numVentasHoy > 0 && <> · ticket promedio {dinero(ticketProm)}</>}
          </span>
        </div>
        {pct != null && (
          <>
            <div style={{ position: 'relative', height: '14px', backgroundColor: '#F1ECFA', borderRadius: '999px' }}
              role="img" aria-label={`Hoy ${dinero(totalHoy)} de un promedio de ${dinero(promedioDiario)}`}>
              <div style={{ width: `${(totalHoy / escala) * 100}%`, height: '100%', backgroundColor: ACENTO, borderRadius: '999px', transition: 'width .6s ease' }} />
              <div title={`Promedio: ${dinero(promedioDiario)}`}
                style={{ position: 'absolute', top: '-4px', bottom: '-4px', left: `${(promedioDiario / escala) * 100}%`, width: '2px', backgroundColor: TINTA, borderRadius: '2px' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: SUAVE, marginTop: '6px', gap: '8px', flexWrap: 'wrap' }}>
              <span>Hoy: <strong style={{ color: TINTA }}>{dinero(totalHoy)}</strong></span>
              <span>Promedio por día (6 días antes): <strong style={{ color: TINTA }}>{dinero(promedioDiario)}</strong></span>
            </div>
          </>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
        <div style={tarjeta}>
          <p style={{ margin: '0 0 2px', fontSize: '13px', fontWeight: 700, color: TINTA }}>Ventas de hoy por hora</p>
          <p style={{ margin: '0 0 10px', fontSize: '12px', color: SUAVE }}>
            {mejorHora != null ? `Tu hora más fuerte esta semana: ${horaTxt(mejorHora)}` : 'A qué hora vendes más'}
          </p>
          <Columnas
            datos={horas}
            resaltar={(p) => p.hora === horaActual}
            etiquetaX={(p) => ((p.hora - desde) % 2 === 0 ? horaCorta(p.hora) : '')}
            tooltip={(p) => (
              <>
                <strong>{horaTxt(p.hora)}</strong><br />
                {dinero(p.monto)} · {p.ventas} venta(s)
              </>
            )}
            vacia={totalHoy > 0 ? null : 'Aún no hay ventas hoy'}
          />
        </div>

        <div style={tarjeta}>
          <p style={{ margin: '0 0 2px', fontSize: '13px', fontWeight: 700, color: TINTA }}>Ventas de los últimos 7 días</p>
          <p style={{ margin: '0 0 10px', fontSize: '12px', color: SUAVE }}>Hoy en color fuerte · toca un día para ver el detalle</p>
          <Columnas
            datos={diasSerie}
            resaltar={(p) => p.esHoy}
            etiquetaX={(p) => (p.esHoy ? 'hoy' : nombreDia(p))}
            tooltip={(p) => (
              <>
                <strong style={{ textTransform: 'capitalize' }}>
                  {p.esHoy ? 'Hoy' : p.dia.toLocaleDateString('es-HN', { weekday: 'long', day: 'numeric', month: 'short' })}
                </strong><br />
                Ventas: {dinero(p.ventas)}
                {verCostos && <><br />Ganancia: {dinero(p.ventas - p.costo)}</>}
                {verGastos && <><br />Gastos: {dinero(p.gastos)}</>}
              </>
            )}
            vacia={dias.every((d) => d.ventas === 0) ? 'Sin ventas en estos 7 días' : null}
          />
        </div>
      </div>
    </div>
  );
}
